import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Users, AlertCircle, TrendingDown } from 'lucide-react';
import { brandDependencyData } from './dependencyData';

// Replaceability Badge
const ReplaceabilityBadge = ({ level }) => {
  const config = {
    low: { bg: 'bg-destructive/20', text: 'text-destructive', border: 'border-destructive/30', label: 'Low (Hard to Replace)' },
    medium: { bg: 'bg-warning/20', text: 'text-warning', border: 'border-warning/30', label: 'Medium' },
    high: { bg: 'bg-stable/20', text: 'text-stable', border: 'border-stable/30', label: 'High (Easier to Replace)' },
  };
  const c = config[level] || config.medium;
  return (
    <Badge className={`${c.bg} ${c.text} ${c.border} border font-semibold text-xs`}>
      {c.label}
    </Badge>
  );
};

// Player Card Component
const PlayerCard = ({ player, rank }) => (
  <Card className="bg-card border-border/50">
    <CardContent className="p-5">
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center text-lg font-bold text-primary">
            {rank}
          </div>
          <div>
            <h3 className="font-semibold text-foreground">{player.name}</h3>
            <p className="text-xs text-muted-foreground">{player.position} • Age {player.age}</p>
          </div>
        </div>
        <ReplaceabilityBadge level={player.replaceability} />
      </div>
      
      <div className="grid grid-cols-2 gap-4 mb-4">
        <div>
          <p className="text-xs text-muted-foreground uppercase tracking-wider">Brand Score</p>
          <p className="text-xl font-bold text-foreground">{player.brandScore}</p>
        </div>
        <div>
          <p className="text-xs text-muted-foreground uppercase tracking-wider">% of Team Value</p>
          <p className="text-xl font-bold text-primary">{player.percentOfTeam}%</p>
        </div>
      </div>
      
      <div className="p-3 rounded-lg bg-destructive/5 border border-destructive/20">
        <div className="flex items-center gap-2 mb-2">
          <TrendingDown className="w-4 h-4 text-destructive" />
          <span className="text-xs font-medium text-foreground">3-Month Unavailability Impact</span>
        </div>
        <div className="grid grid-cols-2 gap-2 text-sm">
          <div>
            <span className="text-muted-foreground">Brand Decline: </span>
            <span className="font-semibold text-destructive">-{player.injuryImpact.brandDecline} pts</span>
          </div>
          <div>
            <span className="text-muted-foreground">Value Impact: </span>
            <span className="font-semibold text-destructive">-${player.injuryImpact.valueDecline}M</span>
          </div>
        </div>
      </div>
    </CardContent>
  </Card>
);

export const ReplaceabilityScore = () => {
  const { players, teamBaseline } = brandDependencyData;
  const topPlayer = players[0];
  
  return (
    <section className="mb-10" data-testid="replaceability-section">
      <div className="flex items-center gap-3 mb-6">
        <Users className="w-6 h-6 text-warning" />
        <h2 className="text-lg font-semibold text-foreground tracking-tight">Replaceability Score</h2>
      </div>
      
      {/* Summary Card */}
      <Card className="bg-destructive/5 border-destructive/20 mb-6">
        <CardContent className="p-5">
          <div className="flex items-start gap-4">
            <div className="p-2 rounded-lg bg-destructive/10">
              <AlertCircle className="w-5 h-5 text-destructive" />
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-foreground mb-2">Key Dependency Risk</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">
                If <span className="font-semibold text-foreground">{topPlayer.name}</span> unavailable for 3 months → 
                projected team brand decline: <span className="font-bold text-destructive">-{topPlayer.injuryImpact.brandDecline} pts</span> / 
                <span className="font-bold text-destructive"> -${topPlayer.injuryImpact.valueDecline}M</span> value
              </p>
              <div className="mt-3 flex flex-wrap gap-4 text-xs">
                <div>
                  <span className="text-muted-foreground">Current Team Score: </span>
                  <span className="font-semibold text-foreground">{teamBaseline.brandScore}</span>
                </div>
                <div>
                  <span className="text-muted-foreground">Projected Score: </span>
                  <span className="font-semibold text-warning">{teamBaseline.brandScore - topPlayer.injuryImpact.brandDecline}</span>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
      
      {/* Top 3 Players Grid */}
      <div className="grid md:grid-cols-3 gap-5">
        {players.map((player, index) => (
          <PlayerCard key={player.id} player={player} rank={index + 1} />
        ))}
      </div>
    </section>
  );
};

export default ReplaceabilityScore;
